import React, { useMemo, useState } from "react";

import { useDragLayer, useDrop } from "react-dnd";

import SchedulingTimelineItemRenderer from "./SchedulingTimelineItemRenderer";
import TemplateTimelineItemRenderer from "./TemplateTimelineItemRenderer";
import { TimelineRenderTypes } from "./timeline";
import { DRAG_TYPE } from "../../../constants/dragTypes";
import { SCHEDULING_TIMELINE_ITEM_Z_INDEX } from "../../../constants/fast";
import { getTimelineItemPaddings } from "../../../utils/fast";

import type { CSSProperties, JSX } from "react";

import type {
  SchedulingItemRendererProps,
  TemplatingItemRendererProps,
} from "./ItemRenderer";
import type { MediaLibraryDragItem } from "../../../types";

interface CollectedDragProps {
  isOver: boolean;
  canDrop: boolean;
}

type SchedulingMediaItemRendererProps = Pick<
  SchedulingItemRendererProps,
  | "itemContext"
  | "getItemProps"
  | "item"
  | "dropzoneAcceptedFormats"
  | "isReplaceMode"
  | "timelineRef"
  | "onReplaceItem"
  | "onDeleteItem"
  | "renderType"
>;

type TemplatingMediaItemRendererProps = Pick<
  TemplatingItemRendererProps,
  | "itemContext"
  | "getItemProps"
  | "item"
  | "dropzoneAcceptedFormats"
  | "isReplaceMode"
  | "timelineRef"
  | "onReplaceItem"
  | "onDeleteItem"
  | "renderType"
>;

type MediaItemRendererProps =
  | TemplatingMediaItemRendererProps
  | SchedulingMediaItemRendererProps;

type ConditionalMediaItemRendererProps<T> =
  T extends TimelineRenderTypes.TEMPLATING
    ? TemplatingItemRendererProps
    : T extends TimelineRenderTypes.SCHEDULING
      ? SchedulingItemRendererProps
      : never;

function MediaItemRenderer<T extends MediaItemRendererProps>({
  itemContext,
  getItemProps,
  item,
  isReplaceMode = false,
  dropzoneAcceptedFormats = [],
  timelineRef,
  onReplaceItem,
  onDeleteItem,
  renderType,
}: ConditionalMediaItemRendererProps<T["renderType"]>): JSX.Element {
  const [showModalReplaceItem, setShowModalReplaceItem] = useState(false);
  const [dropMedia, setDropMedia] = useState<MediaLibraryDragItem | null>(null);

  const isVerticalTimeline = timelineRef?.current?.props.isVertical ?? false;
  const isLoopTimeline = timelineRef?.current?.props.isLoop ?? false;

  const styleContainerItem: CSSProperties = {
    background: "transparent",
    border: "none",
  };

  const itemProps = getItemProps({
    style: styleContainerItem,
  });

  const { style, key } = itemProps;

  const handleDropMedia = (itemDrop: MediaLibraryDragItem): void => {
    setDropMedia(itemDrop);
    setShowModalReplaceItem(true);
  };

  const [{ canDrop, isOver }, dropRef] = useDrop<
    MediaLibraryDragItem,
    unknown,
    CollectedDragProps
  >(
    () => ({
      accept: dropzoneAcceptedFormats,
      drop: handleDropMedia,
      canDrop: (itemDrag) => item.duration >= itemDrag.duration,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [item.start_time],
  );

  const { isDragging: isDraggingMediaLibraryItem } = useDragLayer(
    (monitor) => ({
      isDragging:
        monitor.isDragging() &&
        typeof monitor.getItemType() === "string" &&
        (monitor.getItemType() as string).includes(
          DRAG_TYPE.MEDIA_LIBRARY_ITEM,
        ),
    }),
  );

  const dropzone = {
    dropRef,
    canDrop,
    isOver,
  };

  const groupSizeList = timelineRef?.current?.state.groupSizeList ?? [];

  const lineSize = groupSizeList[itemContext.dimensions.order.index] ?? 0;

  const getMediaItemRendererByType = (): JSX.Element => {
    switch (renderType) {
      case TimelineRenderTypes.TEMPLATING: {
        return (
          <TemplateTimelineItemRenderer
            dropMedia={dropMedia}
            dropzone={dropzone}
            isDraggingMediaLibraryItem={isDraggingMediaLibraryItem}
            isLoopTimeline={isLoopTimeline}
            isReplaceMode={isReplaceMode}
            isVerticalTimeline={isVerticalTimeline}
            item={item}
            itemContext={itemContext}
            itemProps={itemProps}
            lineSize={lineSize}
            onDeleteItem={onDeleteItem}
            onReplaceItem={onReplaceItem}
            setShowModalReplaceItem={setShowModalReplaceItem}
            showModalReplaceItem={showModalReplaceItem}
          />
        );
      }
      case TimelineRenderTypes.SCHEDULING:
      default: {
        return (
          <SchedulingTimelineItemRenderer
            dropMedia={dropMedia}
            dropzone={dropzone}
            isDraggingMediaLibraryItem={isDraggingMediaLibraryItem}
            isLoopTimeline={isLoopTimeline}
            isReplaceMode={isReplaceMode}
            isVerticalTimeline={isVerticalTimeline}
            item={item}
            itemContext={itemContext}
            itemProps={itemProps}
            lineSize={lineSize}
            onReplaceItem={onReplaceItem}
            setShowModalReplaceItem={setShowModalReplaceItem}
            showModalReplaceItem={showModalReplaceItem}
            timelineRef={timelineRef}
          />
        );
      }
    }
  };

  const zIndex = useMemo(() => {
    if (renderType === TimelineRenderTypes.SCHEDULING) {
      const isVariant = !item.isOnBaseline;
      if (isVariant) {
        return SCHEDULING_TIMELINE_ITEM_Z_INDEX + 1;
      }
      return SCHEDULING_TIMELINE_ITEM_Z_INDEX;
    }

    return style.zIndex;
  }, [item, renderType, style.zIndex]);

  return (
    <div
      key={key}
      style={{
        ...style,
        lineHeight: "unset",
        cursor: "default",
        ...getTimelineItemPaddings({ isVerticalTimeline, width: style.width }),
        zIndex,
      }}
    >
      {getMediaItemRendererByType()}
    </div>
  );
}

export default MediaItemRenderer;
